//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";
// chrome-feed imports
import { fragments } from "./ChromeFeed.gql";
import { IProps, IState, IContainer } from "./ChromeFeed.d";
import * as styles from "./ChromeFeed.styles";
// other components
import { LocalStorageAlertCard } from "@components/local-storage-alert-card";
import { SuggestionsFeed } from "@components/suggestions-feed";
import { ChromeAutopilotFeedStatusDefragCardRenderer } from "@components/chrome-autopilot-feed-status-defrag-card-renderer";
import { A } from "@ui";
// state
import { IReduxState } from "@reducers/root.reducer";
import { initialFeedState } from "@reducers/chrome-feed.reducer";
import { setLastViewedFeedMillis } from "@actions/chrome-feed.actions";
import { resetNotificationCount } from "@actions/chrome-extension.actions";
import { jwt } from "@state/local-storage";
// mutations
import { updateSetting } from "@mutations";
// util
import { getCurrentOrg } from "@util/org.util";
import { lastViewedFeedMillisSetting } from "@util/org-settings.util";
import { isImpersonated } from "@clockwise/client-commons/src/util/jwt";
// img
import { personWorkingUnderCalendar } from "@img/illustrations";
// constants
import { articleUrls } from "@clockwise/client-commons/src/constants/help-center";
import { TranslationContext } from "@clockwise/client-commons/src/providers/TranslationContext";

//////////////////
// COMPONENT
//////////////////
export class ChromeFeedCmpt extends React.Component<IProps, IState> {
  private ignoreNextSettingUpdate = false;

  // ~-~-~-~-~-~-~-
  // Setup
  // ~-~-~-~-~-~-~-
  constructor(props: IProps) {
    super(props);

    this.state = {
      suggestionCardCount: undefined,
      defragCardCount: undefined,
      localStorageCardCount: undefined,
    };
  }

  static contextType = TranslationContext;
  declare context: React.ContextType<typeof TranslationContext>;

  // ~-~-~-~-~-~-~-
  // Lifecycle
  // ~-~-~-~-~-~-~-
  public UNSAFE_componentWillReceiveProps(nextProps: IProps) {
    const org = getCurrentOrg(this.props.viewer);
    const nextOrg = getCurrentOrg(nextProps.viewer);
    const setting = lastViewedFeedMillisSetting.get(org);
    const nextSetting = lastViewedFeedMillisSetting.get(nextOrg);

    if (!setting || !nextSetting) {
      return;
    }

    if (
      (nextProps.chromeExtensionOpen && !this.props.chromeExtensionOpen) ||
      (setting.value[0] !== nextSetting.value[0] && !this.ignoreNextSettingUpdate)
    ) {
      this.maybeUpdateLastViewedMillis(
        nextOrg,
        nextSetting,
        nextProps.chromeExtensionOpen,
        nextProps.lastViewedFeedMillis,
      );
    } else if (setting.value[0] !== nextSetting.value[0] && this.ignoreNextSettingUpdate) {
      this.ignoreNextSettingUpdate = false;
    }
  }

  public componentDidMount() {
    const org = getCurrentOrg(this.props.viewer);
    const setting = lastViewedFeedMillisSetting.get(org);

    this.maybeUpdateLastViewedMillis(
      org,
      setting,
      this.props.chromeExtensionOpen,
      this.props.lastViewedFeedMillis,
    );
  }

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  private maybeUpdateLastViewedMillis(
    org: ISchema.IOrg | null,
    setting: ISchema.ISetting | undefined,
    chromeExtensionOpen: boolean,
    lastViewedFeedMillis: number,
  ) {
    if (!org || !setting || isImpersonated(jwt.get())) {
      return;
    }

    if (!chromeExtensionOpen || lastViewedFeedMillis === initialFeedState.lastViewedFeedMillis) {
      this.props.setLastViewedFeedMillis(lastViewedFeedMillisSetting.getMillis(org));
      return;
    }

    const now = Date.now();

    this.props.setLastViewedFeedMillis(now);
    this.props.resetNotificationCount();

    this.ignoreNextSettingUpdate = true;

    updateSetting(
      this.props.relay.environment,
      {
        value: [now.toString()],
        setting,
      },
      () => null,
      () => null,
    );
  }

  private updateSuggestionsCardCount = (suggestionCardCount: number) => {
    this.setState({ suggestionCardCount });
  };

  private updateDefragCardCount = (defragCardCount: number) => {
    this.setState({ defragCardCount });
  };

  private updateLocalStorageCardCount = (localStorageCardCount: number) => {
    this.setState({ localStorageCardCount });
  };

  // ~-~-~-~-~-~-~-
  // Rendering
  // ~-~-~-~-~-~-~-
  private maybeRenderEmptyState = (_org: ISchema.IOrg) => {
    const t = this.context;
    const { suggestionCardCount, defragCardCount, localStorageCardCount } = this.state;
    if (
      suggestionCardCount === undefined ||
      defragCardCount === undefined ||
      localStorageCardCount === undefined ||
      (suggestionCardCount || 0) + (defragCardCount || 0) + (localStorageCardCount || 0) > 0
    ) {
      return "";
    }

    return (
      <div style={styles.emptyContainer}>
        <img src={personWorkingUnderCalendar} style={{ width: "100%" }} />
        <div style={styles.emptyText}>{t("feed:assistant-empty")}</div>
        <A href={articleUrls.autopilot} target="_blanks">
          {t("feed:autopilot-link")}
        </A>
      </div>
    );
  };

  // render the content of the feed, with onboarding cards if any
  private renderFeed = (
    org: ISchema.IOrg,
    relayEnvironment: IProps["relay"]["environment"],
    fastCrawlFinished: boolean,
  ) => {
    const orgTimeZone =
      org.orgMeetingHours &&
      org.orgMeetingHours.workingHours &&
      org.orgMeetingHours.workingHours.timeZone;

    return (
      <div style={styles.content}>
        <LocalStorageAlertCard updateCardCount={this.updateLocalStorageCardCount} />
        <ChromeAutopilotFeedStatusDefragCardRenderer
          fastCrawlFinished={fastCrawlFinished}
          orgRelayId={org.id}
          orgTimeZone={orgTimeZone}
          environment={this.props.relay.environment}
          updateCardCount={this.updateDefragCardCount}
        />
        <SuggestionsFeed
          environment={relayEnvironment}
          updateCardCount={this.updateSuggestionsCardCount}
        />
        {this.maybeRenderEmptyState(org)}
      </div>
    );
  };

  public render() {
    // check for data requirements
    const { viewer, relay, fastCrawlFinished } = this.props;
    const org = getCurrentOrg(viewer);

    if (!viewer.user || !org) {
      return <div />;
    }

    return this.renderFeed(org, relay.environment, fastCrawlFinished);
  }
}

//////////////////
// REDUX
//////////////////
function mapStateToProps(state: IReduxState, _ownProps: IContainer) {
  return {
    calendarSelectionStates: state.webExtension.calendarSelectionStates,
    chromeExtensionOpen: state.webExtension.webExtensionOpen,
    lastViewedFeedMillis: state.chromeFeed.lastViewedFeedMillis,
  };
}

const reduxContainer = connect(mapStateToProps, {
  setLastViewedFeedMillis,
  resetNotificationCount,
})(ChromeFeedCmpt);

//////////////////
// RELAY
//////////////////
export const ChromeFeed = createFragmentContainer(reduxContainer, fragments);
